import React from 'react';
import { theme } from '../theme';

/**
 * MottoButtons — v0.32.0.
 *
 * The three actions that ARE the product: Drop a Plan · Drop a Pin ·
 * Send a Report. Every screen that exposes this trio uses THIS
 * component so the motto reads identically everywhere — same labels,
 * same order, same pin-themed icons matching the cover-page hero.
 *
 * Icons are inline SVGs (gradient pin-head + shaft + shadow) rather
 * than lucide-react flat outlines, so they carry the visual weight
 * of the brand pins on the home blueprint hero.
 *
 * Props:
 *   onDropPlan   () => void   — usually navigate or open the plans modal
 *   onDropPin    () => void   — usually navigate to the plan canvas
 *   onSendReport () => void   — usually navigate to the report generator
 *   layout       'row' | 'tiles' (default 'row')
 *                 'row'   = compact pills in a horizontal row, suits a header
 *                 'tiles' = full-width tiles, suits a project-home hero
 *   disable      { plan?: bool, pin?: bool, report?: bool } — optional gate
 *   primaryKey   'plan' | 'pin' | 'report' (default 'report')
 *                 which button gets the brand-orange fill
 *   counts       { plan?: number, pin?: number, report?: number } — small
 *                 count badge shown on the button (v0.34.0: used for the
 *                 plans pill so the user sees "3 plans" at a glance)
 */
// v0.207.0 — the Videos glyph. Same 24-box and same 2px stroke as its
// siblings so the row reads as one set: a film frame, with the pin the others
// carry.
function ScanVideo() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="5" width="13" height="12" rx="2" />
      <path d="M15 9.5 21 6.5v11l-6-3z" />
      <circle cx="8.5" cy="11" r="1.6" />
    </svg>
  );
}

export default function MottoButtons({
  onDropPlan, onDropPin, onSendReport,
  // v0.202.0 — OPTIONAL fourth action. Brett, having finally been shown the
  // right screen: "It shouldnt be there it should be within the report next to
  // drop a pin" — and the row he meant is THIS component, on the defect
  // register, not the tile grid on the project screen. Dropping pins and
  // scanning the floor are the same job, so Scan Floor sits directly after
  // Drop a Pin.
  // OPT-IN ON PURPOSE: only a caller that passes onScanFloor gets the button,
  // so ReportScreen and PlansManagerModal — the other two users of this
  // component — render exactly what they rendered before.
  onScanFloor,
  // v0.207.0 — Brett: "Can you add a spot where i can select the videos from my
  // device and save it on the job for processing." The clips and the passes on
  // the plan are the same job, so Videos sits directly after Scan Floor. Opt-in
  // for the same reason onScanFloor is: the other callers render what they did.
  onScanVideos,
  layout = 'row',
  disable = {},
  primaryKey = 'report',
  counts = {},
}) {
  const buttons = [
    { key: 'plan',   label: 'Drop a Plan',   icon: <PlanPin   />, onClick: onDropPlan,   disabled: !!disable.plan,   count: counts.plan },
    { key: 'pin',    label: 'Drop a Pin',    icon: <DropPin   />, onClick: onDropPin,    disabled: !!disable.pin,    count: counts.pin },
    ...(onScanFloor
      ? [{ key: 'scan', label: 'Scan Floor', icon: <ScanFloor />, onClick: onScanFloor, disabled: !!disable.scan, count: counts.scan }]
      : []),
    ...(onScanVideos
      ? [{ key: 'videos', label: 'Scan Videos', icon: <ScanVideo />, onClick: onScanVideos, disabled: !!disable.videos, count: counts.videos }]
      : []),
    { key: 'report', label: 'Send a Report', icon: <ReportPin />, onClick: onSendReport, disabled: !!disable.report, count: counts.report },
  ];

  if (layout === 'tiles') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${buttons.length}, 1fr)`, gap: 14 }}>
        {buttons.map(b => <MottoTile key={b.key} {...b} primary={b.key === primaryKey} />)}
      </div>
    );
  }

  // Default: row of compact pill-style buttons. The primary key gets
  // the brand-orange fill so eyes land on the "main" action in context
  // (usually Send a Report from the defect register).
  // v0.125.0 — ss-motto-* class hooks: on a phone the trio was
  // wrapping onto two rows. responsive.css now keeps all three on
  // ONE row (flex 1 1 0, tighter padding/type, label ellipsises
  // before the row ever wraps).
  return (
    <div className="ss-motto-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {buttons.map(b => (
        <MottoPill
          key={b.key}
          {...b}
          primary={b.key === primaryKey}
        />
      ))}
    </div>
  );
}

// ───────── pill (compact, header use) ─────────
function MottoPill({ label, icon, onClick, disabled, primary, count }) {
  const showCount = typeof count === 'number' && count > 0;
  return (
    <button
      className="ss-motto-pill"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 14px 6px 8px',
        background: primary ? theme.brand : theme.surface,
        color: primary ? 'white' : theme.text,
        border: `1.5px solid ${primary ? theme.brand : theme.border}`,
        borderRadius: 999,
        fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        fontFamily: 'inherit',
        boxShadow: primary ? '0 1px 0 rgba(124,45,18,0.25)' : 'none',
        transition: 'transform 0.06s',
      }}
      onMouseDown={e => e.currentTarget.style.transform = 'translateY(1px)'}
      onMouseUp={e => e.currentTarget.style.transform = 'translateY(0)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <span className="ss-motto-icon" style={{ width: 22, height: 26, display: 'inline-flex' }}>{icon}</span>
      <span className="ss-motto-label">{label}</span>
      {/* v0.34.0 — Count badge. Subtle pill inside the pill, so the
          user sees "3 plans / 11 pins / 2 reports" without leaving
          the motto trio. */}
      {showCount && (
        <span className="ss-motto-count" style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 22, height: 18, padding: '0 6px',
          marginLeft: 2,
          borderRadius: 999,
          background: primary ? 'rgba(255,255,255,0.25)' : theme.bg,
          color: primary ? 'white' : theme.brand,
          fontSize: 11, fontWeight: 700,
          border: primary ? '1px solid rgba(255,255,255,0.4)' : `1px solid ${theme.brand}`,
        }}>{count}</span>
      )}
    </button>
  );
}

// ───────── tile (large, hero use) ─────────
function MottoTile({ label, icon, onClick, disabled, primary }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '22px 14px',
        background: primary ? theme.bg : theme.surface,
        color: theme.text,
        border: `2px solid ${primary ? theme.brand : theme.border}`,
        borderRadius: 12,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        fontFamily: 'inherit',
        transition: 'transform 0.08s, box-shadow 0.08s',
      }}
      onMouseEnter={e => {
        if (disabled) return;
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(15,23,42,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <span style={{ width: 44, height: 52, display: 'inline-flex' }}>{icon}</span>
      <span style={{ fontSize: 15, fontWeight: 700, marginTop: 6 }}>{label}</span>
    </button>
  );
}

/* ──────────────────────────────────────────────────────────────────
   PIN GLYPHS — gradient SVGs matching the home blueprint hero.
   IDs are component-scoped (suffixed) so two MottoButtons on the
   same page don't collide.
   ────────────────────────────────────────────────────────────────── */

// Generic pin-head + shaft used by Drop-a-Pin and Send-a-Report.
function pinGradient(idHead, idShaft, headColor = '#e8650a') {
  const lighter = '#ff9a4d';
  const darker  = '#7c2d12';
  return (
    <defs>
      <radialGradient id={idHead} cx="35%" cy="30%" r="70%">
        <stop offset="0"   stopColor={lighter} />
        <stop offset="0.6" stopColor={headColor} />
        <stop offset="1"   stopColor={darker} />
      </radialGradient>
      <linearGradient id={idShaft} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0"   stopColor="#d4d4d8" />
        <stop offset="0.5" stopColor="#9ca3af" />
        <stop offset="1"   stopColor="#374151" />
      </linearGradient>
    </defs>
  );
}

// Drop a Plan — pin on a small blueprint sheet (same iconography
// as HomeScreen's TileIcon name="plan").
function PlanPin() {
  return (
    <svg viewBox="0 0 32 38" width="100%" height="100%" aria-hidden="true">
      {/* paper sheet, tilted slightly so it reads as a real sheet */}
      <rect x="3" y="6" width="26" height="22" rx="1.5"
            fill="#0e3a6b" stroke="#0a2a52" strokeWidth="0.5" />
      <g stroke="#5fa3d6" strokeWidth="0.35" opacity="0.55">
        <line x1="3"  y1="13" x2="29" y2="13" />
        <line x1="3"  y1="19" x2="29" y2="19" />
        <line x1="11" y1="6"  x2="11" y2="28" />
        <line x1="20" y1="6"  x2="20" y2="28" />
      </g>
      <g stroke="#cfe4f5" fill="none" strokeWidth="0.8">
        <path d="M 6 10 L 6 25 L 26 25 L 26 16 L 19 16 L 19 10 Z" />
      </g>
      {/* small red pin marker sitting on the sheet */}
      <circle cx="15" cy="14" r="2" fill="#dc2626" stroke="#7f1d1d" strokeWidth="0.4" />
      <path d="M 15 16 L 14 19 L 16 19 Z" fill="#dc2626" />
    </svg>
  );
}

// Drop a Pin — the classic 3D map pin in brand orange (matches the
// hero floorplan's main pins).
function ScanFloor() {
  return (
    <svg viewBox="0 0 32 38" width="100%" height="100%" aria-hidden="true">
      <ellipse cx="16" cy="35" rx="6" ry="1.3" fill="#000" opacity="0.22" />
      {/* the floor: four tiles with their grout lines */}
      <rect x="4" y="9" width="24" height="20" rx="1.5" fill="#e5e7eb" stroke="#1f2937" strokeWidth="0.8" />
      <line x1="16" y1="9" x2="16" y2="29" stroke="#9ca3af" strokeWidth="0.9" />
      <line x1="4" y1="19" x2="28" y2="19" stroke="#9ca3af" strokeWidth="0.9" />
      {/* the strip you have covered, raked across it */}
      <rect x="4" y="13.5" width="24" height="5" fill="#e8650a" opacity="0.75" />
      <path d="M 4 33 L 16 31 L 28 33" fill="none" stroke="#e8650a" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function DropPin() {
  const ids = useScopedIds('drop-pin');
  return (
    <svg viewBox="0 0 32 38" width="100%" height="100%" aria-hidden="true">
      {pinGradient(ids.head, ids.shaft, '#e8650a')}
      <ellipse cx="16" cy="35" rx="6" ry="1.3" fill="#000" opacity="0.22" />
      <path d="M 14 14 L 16 35 L 18 14 Z" fill={`url(#${ids.shaft})`}
            stroke="#1f2937" strokeWidth="0.4" />
      <circle cx="16" cy="11" r="8.5" fill={`url(#${ids.head})`}
              stroke="#7c2d12" strokeWidth="0.6" />
      <ellipse cx="13" cy="8" rx="2.2" ry="1.8" fill="#fff" opacity="0.55" />
    </svg>
  );
}

// Send a Report — pin attached to a tiny report sheet.
function ReportPin() {
  const ids = useScopedIds('report-pin');
  return (
    <svg viewBox="0 0 32 38" width="100%" height="100%" aria-hidden="true">
      {pinGradient(ids.head, ids.shaft, '#16a34a')}
      {/* report sheet at the back */}
      <rect x="9" y="8" width="18" height="22" rx="1.5"
            fill="white" stroke="#94a3b8" strokeWidth="0.7" />
      <g stroke="#cbd5e1" strokeWidth="0.6">
        <line x1="12" y1="14" x2="24" y2="14" />
        <line x1="12" y1="18" x2="24" y2="18" />
        <line x1="12" y1="22" x2="20" y2="22" />
      </g>
      {/* pin sticking out from the top-left */}
      <ellipse cx="10" cy="33" rx="5" ry="1.1" fill="#000" opacity="0.22" />
      <path d="M 8 14 L 10 33 L 12 14 Z" fill={`url(#${ids.shaft})`}
            stroke="#1f2937" strokeWidth="0.35" />
      <circle cx="10" cy="11" r="7" fill={`url(#${ids.head})`}
              stroke="#14532d" strokeWidth="0.6" />
      <ellipse cx="8" cy="8.5" rx="1.7" ry="1.4" fill="#fff" opacity="0.55" />
    </svg>
  );
}

// Make each SVG's gradient ids unique per render so two MottoButtons
// on the same page don't both try to register 'drop-pin-head' on the
// document and produce gray balls.
function useScopedIds(prefix) {
  const id = React.useId ? React.useId() : Math.random().toString(36).slice(2, 8);
  return { head: `${prefix}-head-${id}`, shaft: `${prefix}-shaft-${id}` };
}
